export const FACTIONS = {
    cta: {
        id: 'cta',
        name: 'Commonwealth Trade Authority',
        tagline: 'The regulator',
        blurb: 'Standards, certification, and the blue-seal stamp on every legal charter. ' +
            'No rivals — everyone has to play nice with the CTA.',
        paletteAccent: 'crt-blue',
        rival: null,
    },
    helion: {
        id: 'helion',
        name: 'Helion Dynamics',
        tagline: 'Energy megacorp',
        blurb: 'Fusion cores, stellar capacitors, pristine reactor output. ' +
            'Thinks Vega Combine is selling glorified scrap to the desperate.',
        paletteAccent: 'crt-amber',
        rival: 'vega',
    },
    argent: {
        id: 'argent',
        name: 'Argent Foundries',
        tagline: 'Metals megacorp',
        blurb: 'Alloys, superalloys, precision bearings. Wants Tycho to stop handling ' +
            'their freight with whatever drone is cheap that week.',
        paletteAccent: 'brass',
        rival: 'tycho',
    },
    meridian: {
        id: 'meridian',
        name: 'Meridian Biotech',
        tagline: 'Biotech megacorp',
        blurb: 'Cultures, pharmaceuticals, grown goods. Constantly at odds with CTA ' +
            'over certification timelines and biosafety thresholds.',
        paletteAccent: 'crt-green',
        rival: 'cta',
    },
    tycho: {
        id: 'tycho',
        name: 'Tycho Industrial',
        tagline: 'Logistics megacorp',
        blurb: 'Routes, contracts, cold chains. Thinks Argent forges slower than a glacier ' +
            'and charges more than stellarium for the privilege.',
        paletteAccent: 'ink',
        rival: 'argent',
    },
    vega: {
        id: 'vega',
        name: 'Vega Combine',
        tagline: 'Scrap / salvage megacorp',
        blurb: 'Reconditioned parts, recycled alloys, half-price with a warranty asterisk. ' +
            'Helion dismisses them publicly and undercuts them privately.',
        paletteAccent: 'crt-red',
        rival: 'helion',
    },
};
/** +10 with your picked faction. */
export const STARTING_FACTION_REP_BONUS = 10;
/** −2 with your picked faction's rival (if any). */
export const STARTING_FACTION_REP_PENALTY = -2;
/**
 * Compute the rep deltas applied to a fresh charter based on the starting
 * faction choice. Returns an empty array for `null` (Independent).
 */
export function startingFactionRepDeltas(choice) {
    if (choice === null)
        return [];
    const meta = FACTIONS[choice];
    const deltas = [{ faction: choice, delta: STARTING_FACTION_REP_BONUS }];
    if (meta.rival !== null) {
        deltas.push({ faction: meta.rival, delta: STARTING_FACTION_REP_PENALTY });
    }
    return deltas;
}
// ── P7 — contract completion / expiry rep propagation ───────────────────
/** Cross-faction propagation factor — rivals lose 30% of what the issuer gains. */
export const CONTRACT_RIVAL_REP_FACTION = 0.3;
/**
 * Return the full set of rep deltas for a contract event. The issuer always
 * moves by `delta`; the issuer's rival (if any) moves by `−delta × FACTOR`
 * to model the lore's "doing business with X annoys their rival" dynamic.
 *
 * Used for both completions (positive `delta`) and expiries (negative `delta`)
 * — the rival's sign flips automatically because `−(−delta) = +delta × factor`,
 * which lines up with "letting an issuer down quietly pleases their rival".
 */
export function contractCompletionRepDeltas(issuer, delta) {
    const out = [{ faction: issuer, delta }];
    const rivalId = FACTIONS[issuer].rival;
    if (rivalId !== null) {
        const rivalDelta = -delta * CONTRACT_RIVAL_REP_FACTION;
        if (Math.abs(rivalDelta) >= 0.01) {
            out.push({ faction: rivalId, delta: rivalDelta });
        }
    }
    return out;
}
// ── P10 — Faction signature recipes + payout multipliers ────────────────
/**
 * P10 — Each megacorp's "signature" resource pool: the things they're
 * known for. A Helion contract for refined-fuel feels in-character;
 * one for raw iron-ore is a generalist fallback. The contract-pool
 * generator weights toward the issuer's signature pool when picking a
 * goal resource, giving each faction a recognizable contract identity.
 *
 * CTA is the regulator — it intentionally has NO signature pool; its
 * contracts can be anything in the catalog. That keeps CTA as the
 * dependable generalist source even when the player's reputation is
 * extreme with the megacorps.
 *
 * Tier-0 ores live mostly in Vega's pool (they're the salvage corp);
 * the higher-tier refined goods cluster around their thematic owner.
 */
export const FACTION_SIGNATURE_RESOURCES = {
    cta: [], // generalist — no preferred pool
    helion: ['refined-fuel', 'fermented-fuel', 'plasma-cell', 'lithium-ion-module'],
    argent: ['iron-ingot', 'copper-plate', 'precision-bearing', 'superalloy', 'ceramic-composite'],
    meridian: ['bio-mass', 'fermented-fuel', 'pharma-precursor', 'antibiotic', 'synth-protein'],
    tycho: ['copper-wire', 'basic-circuit', 'semiconductor-wafer'],
    vega: ['iron-ore', 'coal', 'copper-ore', 'bauxite'],
};
/**
 * Multiplier applied to a contract's payout when the goal resource is
 * one of the issuer's signature resources. > 1.0 ⇒ premium for staying
 * in-faction. Compounds with the existing reputation multiplier.
 */
export const FACTION_SIGNATURE_PAYOUT_MULT = 1.3;
/**
 * Multiplier when the issuer asks for an OFF-signature resource (e.g.
 * Helion asking for iron-ingot). Slightly reduces the payout — it's a
 * "we'll take it but it's not what we're known for" deal. < 1.0 ⇒
 * gentle nudge toward in-faction work without making mismatched
 * contracts unprofitable.
 *
 * CTA is exempt — it has no signature pool, so every CTA contract is
 * priced at 1.0×.
 */
export const FACTION_OFF_SIGNATURE_PAYOUT_MULT = 0.9;
/**
 * Compute the per-issuer payout multiplier for a contract. Returns 1.0
 * for CTA (no signature pool), the bonus mult when the resource is in
 * the issuer's signature pool, otherwise the off-signature mult.
 */
export function factionSignatureMultiplier(issuer, resource) {
    if (issuer === 'cta')
        return 1.0;
    const sig = FACTION_SIGNATURE_RESOURCES[issuer];
    if (sig.length === 0)
        return 1.0;
    return sig.includes(resource) ? FACTION_SIGNATURE_PAYOUT_MULT : FACTION_OFF_SIGNATURE_PAYOUT_MULT;
}
// ── P10 — Issuer-pool selection ─────────────────────────────────────────
/**
 * Faction issuance state for the contract-pool generator. Hostile
 * factions (rep ≤ this threshold) won't issue contracts at all —
 * burning bridges with Argent means no Argent contracts until you
 * mend the relationship.
 */
export const FACTION_HOSTILE_REP_CUTOFF = -50;
/**
 * Per-faction base weight when picking who issues an offer. CTA gets
 * the largest base because it's the regulator — always at the table.
 * Megacorps split the remainder roughly evenly. Reputation scales
 * these at runtime: high rep ⇒ more offers from that corp.
 */
export const FACTION_BASE_WEIGHT = {
    cta: 4,
    helion: 2,
    argent: 2,
    meridian: 2,
    tycho: 2,
    vega: 2,
};
/**
 * Reputation → weight multiplier. Negative rep down-weights an issuer
 * (hostile factions stop issuing entirely below `FACTION_HOSTILE_REP_CUTOFF`);
 * positive rep up-weights them so a player who's grinded Helion sees
 * mostly Helion contracts.
 *
 *   rep -100 → 0.0 (hostile cutoff catches it first anyway)
 *   rep    0 → 1.0 (neutral baseline)
 *   rep +100 → 2.5 (heavy lean toward favored corps)
 */
export function factionWeightFromRep(rep) {
    const r = Math.max(-100, Math.min(100, rep));
    if (r >= 0)
        return 1 + (r / 100) * 1.5; // 1 → 2.5
    return 1 + (r / 100) * 1; // 1 → 0
}
// ── P10 — Defection mechanic ────────────────────────────────────────────
/**
 * Cost in credits to switch your starting faction (defect). Steep
 * enough to feel like a real commitment; not so steep it's only
 * available at very high tiers. Plays as "buy out your old contracts +
 * re-certify with the new megacorp."
 */
export const DEFECTION_CREDIT_COST = 50_000;
/**
 * P10 v1.5 — minimum ticks between consecutive defections. World runs
 * at 5 Hz (5 ticks/second), so 1500 ticks ≈ 5 minutes wall-clock,
 * which the lore's compressed calendar reads as "seven in-game days."
 * Stops thrashing between factions to chase short-lived faction-bonus
 * events. The credit cost still applies on top — defecting twice
 * means paying twice AND waiting out the window.
 */
export const DEFECTION_COOLDOWN_TICKS = 1500;
// ── P10 — Reputation discount on research ───────────────────────────────
/**
 * Reputation threshold above which a faction's "patron discount"
 * kicks in. Players who've grinded Helion to +75 rep get a discount
 * on every research node — the megacorp is bankrolling their R&D.
 */
export const FACTION_PATRON_REP_THRESHOLD = 75;
/**
 * Discount fraction applied when ANY faction's rep meets the patron
 * threshold. Multiplicative on the research node's costRP — at 0.2,
 * a 1000-RP node costs 800 RP if you have a patron. Flat across all
 * patrons (highest patron wins), no stacking.
 */
export const FACTION_PATRON_RESEARCH_DISCOUNT = 0.2;
/**
 * Compute the cost-multiplier the research purchaser should apply.
 * Returns 1.0 if no faction is at patron threshold, else the
 * discounted multiplier.
 */
export function patronResearchDiscount(reputation) {
    for (const r of Object.values(reputation)) {
        if ((r ?? 0) >= FACTION_PATRON_REP_THRESHOLD) {
            return 1 - FACTION_PATRON_RESEARCH_DISCOUNT;
        }
    }
    return 1.0;
}
// ── P10 v2 — Per-spec blueprint discounts ──────────────────────────────────
/**
 * Per-faction "signature" machine specs. Each faction has a stable list
 * of machine kinds whose blueprints they specialise in. When the player
 * is patron of that faction (rep ≥ FACTION_PATRON_REP_THRESHOLD), the
 * faction subsidises the build cost — ₡1000 smelter costs ₡850 if you
 * have an Argent patronage.
 *
 * Hand-curated per the lore §2 archetype. CTA stays generalist (only
 * trade-port — they're the regulator, not a manufacturer).
 *
 * The map is `unknown[]` rather than `MachineSpecId[]` to dodge a
 * circular type import; the caller validates ids against MACHINE_SPECS
 * before consuming them. Stable list; new faction-themed machines
 * extend the relevant entry.
 */
export const FACTION_SIGNATURE_SPECS = {
    cta: ['trade-port'],
    helion: [
        'chemical-plant',
        'plasma-refinery',
        'capacitor-array',
        'combustion-cell',
        'electrolyzer',
        'nuclear-cell-plant',
        'cryo-separator',
    ],
    argent: [
        'smelter',
        'alloy-smelter',
        'forming-press',
        'machining-center',
        'kiln',
    ],
    meridian: ['algae-reactor', 'fermentor', 'culture-lab', 'bio-reactor-array'],
    tycho: ['wire-extruder', 'assembler', 'wafer-mill', 'matrix-core'],
    vega: ['mining-drill', 'heavy-mining-drill', 'atmospheric-collector'],
};
/**
 * Discount fraction applied to a signature spec's cost when the player
 * is patron of the owning faction. 0.15 = 15% off the build cost. Sized
 * smaller than the research discount (20%) so blueprint patronage
 * compounds with research patronage without making free machines a
 * realistic outcome at high rep.
 */
export const FACTION_PATRON_BLUEPRINT_DISCOUNT = 0.15;
/**
 * Compute the cost-multiplier for placing a specific machine spec.
 * Walks the player's reputation map, looking for any faction that:
 *   1. Has the spec in its signature list, AND
 *   2. Is at or above `FACTION_PATRON_REP_THRESHOLD` rep.
 * Returns the discounted multiplier (1 - FACTION_PATRON_BLUEPRINT_DISCOUNT)
 * for the first match; otherwise 1.0. No stacking — if the player is
 * patron of two factions whose pools overlap, they pay the same single
 * discount.
 */
export function patronBlueprintDiscount(specId, reputation) {
    for (const [faction, pool] of Object.entries(FACTION_SIGNATURE_SPECS)) {
        if (!pool.includes(specId))
            continue;
        if ((reputation[faction] ?? 0) >= FACTION_PATRON_REP_THRESHOLD) {
            return 1 - FACTION_PATRON_BLUEPRINT_DISCOUNT;
        }
    }
    return 1.0;
}
/**
 * Convenience: which faction(s) treat this spec as their signature?
 * Used by the client UI to label "Argent patronage" on a tooltip.
 */
export function signatureFactionsForSpec(specId) {
    const out = [];
    for (const [faction, pool] of Object.entries(FACTION_SIGNATURE_SPECS)) {
        if (pool.includes(specId))
            out.push(faction);
    }
    return out;
}
// ── P7 — payout scaling by reputation ───────────────────────────────────
/**
 * Reputation amplifies (or attenuates) contract payouts. At +100 with the
 * issuer the player earns +50% over baseline; at −100 the contract pays half.
 * Keeps the contract panel meaningful as a long-term axis without making low
 * rep punishingly unsurvivable.
 *
 *   payout × (1 + clamp(rep, −100, 100) / 100 × MAX_BONUS)
 *
 * MAX_BONUS = 0.5 ⇒ ±50% swing across the full rep range.
 */
export const REP_PAYOUT_MAX_BONUS = 0.5;
/**
 * Pure helper — caller passes the player's rep with the issuer (or 0) and
 * gets back a multiplier in [0.5, 1.5]. Used both for live offer pricing and
 * for tests that want to assert the relationship without setting up a Player.
 */
export function reputationPayoutMultiplier(repWithIssuer) {
    const clamped = Math.max(-100, Math.min(100, repWithIssuer));
    return 1 + (clamped / 100) * REP_PAYOUT_MAX_BONUS;
}
//# sourceMappingURL=factions.js.map